import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import moment from "moment";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { captureHandledException } from "src/services/sentry";

import Confirm from "../Confirm";
import { Form, DatePicker, Checkbox } from "../Form";
import FormFieldValue from "../Form/FormFieldValue";
import { updateCertificateDates } from "../../api/certificates";
import { DATE_FORMAT } from "../../constants/dateFormats";

interface PendingCertificate {
  id: number;
  crewId: number;
  userId: number;
  username: string;
  userEmail: string;
  courseId: number;
  name: string;
  completedDate: string | null;
  validPeriod: string | null;
  comments: string | null;
  fileName: string | null;
  isCustom: boolean;
  status: string;
}

interface EditDatesModalProps {
  open: boolean;
  certificate: PendingCertificate | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface ValidPeriodFieldsProps {
  neverExpires: boolean;
  setValidPeriod: (value: string | null) => void;
}

const ValidPeriodFields: FC<ValidPeriodFieldsProps> = ({ neverExpires, setValidPeriod }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (neverExpires) {
      setValidPeriod(null);
    }
  }, [neverExpires]);

  return (
    <>
      <DatePicker
        name="validPeriod"
        label={t("pending_certificates.validity_period")}
        clearable
        disabled={neverExpires}
      />
      <Checkbox name="neverExpires" label={t("certificate_never_expires")} />
    </>
  );
};

const EditDatesModal: FC<EditDatesModalProps> = ({ open, certificate, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = React.useRef<any>(null);

  const handleConfirm = async (confirmed: boolean) => {
    if (!confirmed) {
      onClose();
      return;
    }

    if (!certificate || !formRef.current) return;

    // A rejected date is stored as null with an error; saving it would silently clear the date.
    if (formRef.current.hasErrors()) return;

    const values = formRef.current.values();

    try {
      await updateCertificateDates(
        certificate.crewId,
        certificate.id,
        certificate.isCustom,
        values.completedDate || null,
        values.neverExpires ? null : values.validPeriod || null,
      );
      enqueueSnackbar(t("pending_certificates.update_success"), { variant: "success" });
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to update certificate dates:", error);
      captureHandledException(error, {
        component: "PendingCertificateEditDatesModal",
        action: "updateCertificateDates",
        certificateId: certificate.id,
        crewId: certificate.crewId,
        userId: certificate.userId,
      });
      enqueueSnackbar(t("pending_certificates.update_failed"), { variant: "error" });
    }
  };

  if (!certificate || !open) {
    return null;
  }

  return (
    <Confirm title={t("pending_certificates.edit_dates")} onConfirm={handleConfirm}>
      <Form
        ref={formRef}
        defaultValues={{
          completedDate: certificate.completedDate
            ? moment(certificate.completedDate).format(DATE_FORMAT.IO_FORMAT)
            : null,
          validPeriod: certificate.validPeriod ? moment(certificate.validPeriod).format(DATE_FORMAT.IO_FORMAT) : null,
          neverExpires: !certificate.validPeriod,
        }}
        validation={{}}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            gutterBottom
            sx={{
              color: "text.secondary",
            }}
          >
            {t("pending_certificates.member_fullname_email")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ({certificate.userId}) {certificate.username} / {certificate.userEmail}
          </Typography>

          <Typography
            variant="body2"
            gutterBottom
            sx={{
              color: "text.secondary",
            }}
          >
            {t("pending_certificates.course_name")}
          </Typography>
          <Typography variant="body1">
            ({certificate.isCustom ? `cc_${certificate.courseId}` : `${certificate.courseId}`}) {certificate.name}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <DatePicker name="completedDate" label={t("pending_certificates.completed_date")} clearable />
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            <FormFieldValue
              name="neverExpires"
              render={(neverExpiresValue: boolean) => (
                <FormFieldValue
                  name="validPeriod"
                  render={(_validPeriodValue: string | null, setValidPeriodValue: (value: string | null) => void) => (
                    <ValidPeriodFields neverExpires={neverExpiresValue} setValidPeriod={setValidPeriodValue} />
                  )}
                />
              )}
            />
          </Box>
        </Box>
      </Form>
    </Confirm>
  );
};

export default EditDatesModal;
